import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../lib/supabase';

const { FiClock, FiTarget, FiCheck, FiAward, FiUsers, FiStar, FiTrendingUp, FiZap } = FiIcons;

const WelcomeStep = () => {
  const [businessCount, setBusinessCount] = useState(0);
  const [countLoaded, setCountLoaded] = useState(false);

  useEffect(() => {
    // Fetch the count of submissions from Supabase
    const fetchBusinessCount = async () => {
      try {
        const { data, error } = await supabase
          .from('brand_health_submissions_h7f92k3a')
          .select('count', { count: 'exact', head: true });

        if (error) {
          console.error('Error fetching business count:', error);
          // Set a default number if there's an error
          setBusinessCount(104);
        } else {
          // Add 100 to the actual count to make it look more impressive
          const baseCount = data?.count || 0;
          setBusinessCount(baseCount + 100);
        }
        setCountLoaded(true);
      } catch (err) {
        console.error('Unexpected error fetching count:', err);
        setBusinessCount(104);
        setCountLoaded(true);
      }
    };

    fetchBusinessCount();
  }, []);

  // Animate the counter
  const [displayCount, setDisplayCount] = useState(100);

  useEffect(() => {
    if (!countLoaded) return;

    // Start from a lower number and count up
    const startCount = Math.max(100, businessCount - 20);
    setDisplayCount(startCount);

    const interval = setInterval(() => {
      setDisplayCount(current => {
        if (current >= businessCount) {
          clearInterval(interval);
          return businessCount;
        }
        return current + 1;
      });
    }, 50);

    return () => clearInterval(interval);
  }, [businessCount, countLoaded]);

  return (
    <div className="text-center space-y-8 max-w-5xl mx-auto px-4">
      {/* Attention-grabbing header */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="space-y-6"
      >
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="inline-block px-5 py-2 bg-gradient-to-r from-primary-600 to-primary-500 rounded-full shadow-lg"
        >
          <span className="text-white font-bold text-sm uppercase tracking-wider flex items-center gap-2">
            <SafeIcon icon={FiZap} className="w-4 h-4" />
            Free Fitness Brand Health Check
          </span>
        </motion.div>

        <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight">
          Find the <span className="text-primary-600">3 Fixes</span> Blocking Your Next <span className="text-primary-600">20 Leads</span>
        </h1>

        <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
          In just 90 seconds, discover your <span className="text-white font-semibold">Brand Health Score</span> across positioning, website and marketing — plus get a personalized action plan you can implement today.
        </p>

        {/* Trust indicators row */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm text-gray-400"
        >
          <div className="flex items-center gap-1">
            <SafeIcon icon={FiClock} className="w-4 h-4 text-primary-500" />
            <span>90 seconds</span>
          </div>
          <div className="hidden sm:block w-1 h-1 rounded-full bg-gray-600"></div>
          <div className="flex items-center gap-1">
            <SafeIcon icon={FiCheck} className="w-4 h-4 text-primary-500" />
            <span>100% Free</span>
          </div>
          <div className="hidden sm:block w-1 h-1 rounded-full bg-gray-600"></div>
          <div className="flex items-center gap-1">
            <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-primary-500" />
            <span>Instant Results</span>
          </div>
        </motion.div>
      </motion.div>

      {/* Enhanced benefit cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-4xl mx-auto"
      >
        <motion.div
          whileHover={{ scale: 1.05, y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
          className="bg-gradient-to-br from-dark-500 to-dark-600 rounded-xl p-6 shadow-xl border-2 border-dark-400 hover:border-primary-600 transition-all duration-300 soft-shadow inner-shadow group"
        >
          <div className="bg-primary-600 bg-opacity-20 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
            <SafeIcon icon={FiTarget} className="w-7 h-7 text-primary-600" />
          </div>
          <h3 className="font-bold text-white mb-2 text-lg">Identify Your Blockers</h3>
          <p className="text-gray-300 text-sm leading-relaxed">Pinpoint exactly where you're losing leads and revenue</p>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.05, y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
          className="bg-gradient-to-br from-dark-500 to-dark-600 rounded-xl p-6 shadow-xl border-2 border-primary-600 hover:border-primary-500 transition-all duration-300 soft-shadow inner-shadow group"
        >
          <div className="bg-primary-600 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
            <SafeIcon icon={FiZap} className="w-7 h-7 text-white" />
          </div>
          <h3 className="font-bold text-white mb-2 text-lg">Get Instant Fixes</h3>
          <p className="text-gray-300 text-sm leading-relaxed">Receive personalized recommendations based on your unique results</p>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.05, y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
          className="bg-gradient-to-br from-dark-500 to-dark-600 rounded-xl p-6 shadow-xl border-2 border-dark-400 hover:border-primary-600 transition-all duration-300 soft-shadow inner-shadow group"
        >
          <div className="bg-primary-600 bg-opacity-20 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
            <SafeIcon icon={FiCheck} className="w-7 h-7 text-primary-600" />
          </div>
          <h3 className="font-bold text-white mb-2 text-lg">Actionable Next Steps</h3>
          <p className="text-gray-300 text-sm leading-relaxed">Get a clear roadmap you can start implementing today</p>
        </motion.div>
      </motion.div>

      {/* Testimonial Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="max-w-3xl mx-auto"
      >
        <div className="bg-gradient-to-br from-dark-600 to-dark-500 rounded-xl p-6 md:p-8 border-2 border-dark-400 shadow-xl">
          <div className="flex items-center justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-500 fill-current" />
            ))}
          </div>
          <blockquote className="text-gray-200 text-base md:text-lg italic leading-relaxed mb-4">
            "This assessment revealed exactly where we were losing leads. The Top 3 Fixes gave us a clear roadmap. We implemented the recommendations and saw a 40% increase in enquiries within 30 days."
          </blockquote>
          <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold text-lg">
              SC
            </div>
            <div className="text-left">
              <p className="text-white font-semibold">Sarah Collins</p>
              <p className="text-gray-400 text-sm">Owner, Fit Life Studio</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Social Proof Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.7 }}
        className="space-y-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="bg-dark-600 rounded-xl py-4 px-6 inline-block mx-auto soft-shadow border border-dark-400"
        >
          <div className="flex items-center justify-center gap-3">
            <SafeIcon icon={FiUsers} className="w-6 h-6 text-primary-500" />
            <p className="text-gray-200 font-medium text-lg">
              Trusted by <span className="text-primary-500 font-bold text-xl">{displayCount}+</span> fitness businesses
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="flex flex-col items-center justify-center"
        >
          <div className="flex items-center gap-2 mb-2">
            <SafeIcon icon={FiAward} className="w-5 h-5 text-primary-500" />
            <p className="text-gray-300 font-medium">
              Built by <span className="text-white font-semibold">PulseFit Marketing</span>
            </p>
          </div>
          <p className="text-gray-400 text-sm max-w-xl">
            Helping fitness brands grow since 2020 • Expert brand positioning & marketing
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default WelcomeStep;